import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../../../../../Components/Utils/Breadcrumb";
import SearchBar from "../../../../../Components/Utils/SearchBar";
import {
  CreateButton,
  FilterButton,
  CancelButton,
  SubmitButton,
} from "../../../../../Components/Utils/Buttons";
import { useFormik } from "formik";
import FormikSelectTwo from "../../../../../Components/Utils/Select";
import UseGetDataSearch from "../../../../../Components/Hooks/useFetchDataBySearch";
import { useDeleteData } from "../../../../../Components/Hooks/useDeleteData";
import useFetchMataData from "../../../../../Components/Hooks/useFetchMataData";
import ModalContainer from "../../../../../Components/Others/ModalContainer";
import PremiumTable from "../../../../../Components/Others/PremiumTable";
import { GetAccess, GetRole } from "../../../../../Components/Utils/roles";

export default function ParticipantsListPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

  const { value: roleData } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "MeetingParticipant_Role"
  );
  const { value: statusData } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "MeetingParticipant_Status"
  );

  const roleOptions = useMemo(
    () => [{ value: "all", label: "All Roles" }, ...(roleData || [])],
    [roleData]
  );

  const statusOptions = useMemo(
    () => [{ value: "all", label: "All Status" }, ...(statusData || [])],
    [statusData]
  );

  const [filterState, setFilterState] = useState({
    meeting_role: "all",
    status: "all",
  });

  const filterFormik = useFormik({
    initialValues: filterState,
    enableReinitialize: true,
    onSubmit: (values) => {
      setFilterState(values);
      setPageIndex(1);
      setShowFilterDialog(false);
    },
  });

  const clearFilters = () => {
    filterFormik.resetForm({
      values: { meeting_role: "all", status: "all" },
    });
    setFilterState({ meeting_role: "all", status: "all" });
    setShowFilterDialog(false);
    setPageIndex(1);
  };

  const isFiltered =
    (filterState.meeting_role && filterState.meeting_role !== "all") ||
    (filterState.status && filterState.status !== "all");

  const queryParams = useMemo(() => {
    return {
      search: searchQuery || undefined,
      page: pageIndex,
      limit: itemsPerPage,
      meeting_role:
        filterState.meeting_role && filterState.meeting_role !== "all"
          ? filterState.meeting_role
          : undefined,
      status:
        filterState.status && filterState.status !== "all"
          ? filterState.status
          : undefined,
    };
  }, [searchQuery, pageIndex, itemsPerPage, filterState]);

  const { data, totalCount, getData, dataLoading } = UseGetDataSearch(
    "/meetings/participants/",
    queryParams
  );

  const { handle: handleDelete } = useDeleteData(getData);

  const handleConfirmDelete = () => {
    if (!deleteModal.item) return;
    const targetId = deleteModal.item.id || deleteModal.item.participant_id;
    handleDelete(`/meetings/participants/${targetId}`, (val) => {
      setDeleteModal({ open: Boolean(val), item: null });
    });
  };

  const participantsList = useMemo(() => {
    return Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : [];
  }, [data]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "Confirmed":
      case "Attended":
      case "Accepted":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Invited":
      case "Tentative":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "Absent":
      case "Declined":
        return "bg-rose-50 text-rose-700 border-rose-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "Organizer":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Presenter":
        return "bg-indigo-50 text-indigo-700 border-indigo-200";
      case "Facilitator":
        return "bg-teal-50 text-teal-700 border-teal-200";
      default:
        return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  return (
    <div className="w-full h-full p-2 lg:px-4">
      {/* Top Header Bar with Breadcrumb, Search, Filter and Create Button */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <Breadcrumb
          items={[
            { name: "Meeting" },
            { name: "Participants" },
          ]}
        />
        <div className="flex flex-wrap items-center gap-2">
          <SearchBar
            value={searchQuery}
            handleChange={(e) => {
              setSearchQuery(e.target.value);
              setPageIndex(1);
            }}
            placeholder="Search participants..."
          />
          <FilterButton
            handleClick={() => setShowFilterDialog(true)}
            label={isFiltered ? "Filtered" : "Filter"}
          />
          {isFiltered && (
            <FilterButton handleClick={clearFilters} label="Clear" />
          )}
          {(GetAccess("meeting_participants_create") || GetRole()) && (
            <Link to="/meeting/participants/create">
              <CreateButton label="Add Participant" />
            </Link>
          )}
        </div>
      </div>

      <PremiumTable
        data={participantsList}
        total={totalCount}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        dataCount={itemsPerPage}
        setDataCount={setItemsPerPage}
        dataLoading={dataLoading}
        headers={[
          { title: "S.No", width: "60px", align: "center" },
          { title: "NAME", width: "auto" },
          { title: "ROLE", width: "130px" },
          { title: "DEPARTMENT", width: "140px" },
          { title: "DESIGNATION", width: "140px" },
          { title: "EMAIL", width: "180px" },
          { title: "PHONE", width: "140px" },
          { title: "STATUS", width: "120px" },
          { title: "ACTIONS", align: "center", width: "120px" },
        ]}
        renderRow={(row, index) => {
          const targetId = row.id || row.participant_id || index + 1;
          const userName =
            row.name ||
            row.user_name ||
            row.userName ||
            row.user?.name ||
            `User #${row.user_id || targetId}`;

          return (
            <tr
              key={targetId}
              className="hover:bg-gray-50/50 transition-colors"
            >
              <td className="p-4 text-center align-middle border-t border-b border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl text-xs font-semibold text-gray-500">
                {(pageIndex - 1) * itemsPerPage + index + 1}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 font-bold text-xs text-gray-900 max-w-xs truncate">
                {GetAccess("meeting_participants_view") || GetRole() ? (
                  <Link
                    to={`/meeting/participants/view/${targetId}`}
                    state={{
                      data: row,
                      pageIndex: pageIndex,
                      searchQuery: searchQuery,
                    }}
                    className="hover:text-[#048178] hover:underline"
                  >
                    {userName}
                  </Link>
                ) : (
                  <span>{userName}</span>
                )}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 whitespace-nowrap">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getRoleBadge(
                    row.meeting_role || row.role
                  )}`}
                >
                  {row.meeting_role || row.role || "Attendee"}
                </span>
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-gray-600">
                {row.department || "-"}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-gray-600">
                {row.designation || "-"}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-gray-600">
                {row.email ? (
                  <a
                    href={`mailto:${row.email}`}
                    className="text-[#048178] hover:underline"
                  >
                    {row.email}
                  </a>
                ) : (
                  "-"
                )}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-gray-600 whitespace-nowrap">
                {row.phone || "-"}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 whitespace-nowrap">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getStatusBadge(
                    row.status
                  )}`}
                >
                  {row.status || "Invited"}
                </span>
              </td>
              <td className="p-4 text-center align-middle border-t border-b border-r border-gray-200/60 rounded-tr-xl rounded-br-xl">
                <div className="flex items-center justify-center gap-1.5">
                  {(GetAccess("meeting_participants_view") || GetRole()) && (
                    <Link
                      to={`/meeting/participants/view/${targetId}`}
                      state={{
                        data: row,
                        pageIndex: pageIndex,
                        searchQuery: searchQuery,
                      }}
                      className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                      title="View details"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </Link>
                  )}

                  {(GetAccess("meeting_participants_edit") || GetRole()) && (
                    <Link
                      to={`/meeting/participants/edit/${targetId}`}
                      state={{
                        data: row,
                        from: "/meeting/participants",
                        pageIndex: pageIndex,
                        searchQuery: searchQuery,
                      }}
                      className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                      title="Edit Participant"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z"
                        />
                      </svg>
                    </Link>
                  )}

                  {(GetAccess("meeting_participants_delete") || GetRole()) && (
                    <button
                      type="button"
                      onClick={() =>
                        setDeleteModal({ open: true, item: row })
                      }
                      className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center cursor-pointer"
                      title="Delete Participant"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        }}
      />

      {/* Filter Modal Dialog */}
      {showFilterDialog && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray-800 bg-opacity-50 z-[9999] flex justify-center items-center p-3 sm:p-4">
          <div className="animate-scale-in w-full sm:w-[480px] max-w-[95vw] max-h-[90vh] bg-white py-4 px-4 sm:px-6 rounded-xl shadow-lg overflow-y-auto">
            <h2 className="text-base font-bold text-gray-800 mb-4">
              Filter Participants
            </h2>
            <form onSubmit={filterFormik.handleSubmit}>
              <div className="flex flex-col gap-3 mb-4">
                <FormikSelectTwo
                  type="text"
                  label="Meeting Role"
                  placeholder="Choose Role"
                  Options={roleOptions}
                  formik={filterFormik}
                  name="meeting_role"
                  width="w-full"
                />
                <FormikSelectTwo
                  type="text"
                  label="Participant Status"
                  placeholder="Choose Status"
                  Options={statusOptions}
                  formik={filterFormik}
                  name="status"
                  width="w-full"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <CancelButton handleClick={() => setShowFilterDialog(false)} />
                <SubmitButton name="Apply" />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal Dialog */}
      <ModalContainer
        visiable={deleteModal.open}
        title="Warning"
        content="Are You Sure! You want to Delete?"
        handleClick={handleConfirmDelete}
        closeModal={() => setDeleteModal({ open: false, item: null })}
      />
    </div>
  );
}
